import { InputBarContext } from "@app/components/assistant/conversation/input_bar/InputBarContext";
import { useConversationDrafts } from "@app/components/assistant/conversation/input_bar/useConversationDrafts";
import { UserSettingsPopover } from "@app/components/UserSettingsPopover";
import { WorkspacePickerRadioGroup } from "@app/components/WorkspacePicker";
import { useCreateConversationWithMessage } from "@app/hooks/useCreateConversationWithMessage";
import { useDevMode } from "@app/hooks/useDevMode";
import { useSendNotification } from "@app/hooks/useNotification";
import { usePrivacyMask } from "@app/hooks/usePrivacyMask";
import config from "@app/lib/api/config";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useSubmitFunction } from "@app/lib/client/utils";
import {
  forceUserRole,
  sendOnboardingConversation,
  showDebugTools,
} from "@app/lib/development";
import { serializeMention } from "@app/lib/mentions/format";
import { ConversationsUpdatedEvent } from "@app/lib/notifications/events";
import { useAppRouter } from "@app/lib/platform";
import { getConversationRoute } from "@app/lib/utils/router";
import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";
import type { AgentMention, MentionType } from "@app/types/assistant/mentions";
import { isAgentMention } from "@app/types/assistant/mentions";
import type { SubscriptionType } from "@app/types/plan";
import { isDevelopment } from "@app/types/shared/env";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import {
  isOnlyAdmin,
  isOnlyBuilder,
  isOnlyManager,
  isOnlyUser,
} from "@app/types/user";
import { datadogLogs } from "@datadog/browser-logs";
import {
  Avatar,
  Beaker02,
  ChevronDown,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Eye,
  EyeOff,
  Heart,
  Icon,
  Lightbulb04,
  LogOut01,
  MessageChatCircle,
  MessagePlusCircle,
  MessageTextCircle01,
  Separator,
  Shapes,
  Star01,
  Terminal,
  User01,
} from "@dust-tt/sparkle";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface UserMenuProps {
  user: UserTypeWithWorkspaces;
  owner: WorkspaceType;
  subscription: SubscriptionType | null;
}

export function UserMenu({ user, owner, subscription }: UserMenuProps) {
  const { t } = useTranslation();
  const router = useAppRouter();
  const { featureFlags } = useFeatureFlags();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const sendNotification = useSendNotification();
  const devMode = useDevMode();
  const privacyMask = usePrivacyMask();
  const { clearAllDraftsFromUser } = useConversationDrafts({
    workspaceId: owner.sId,
    userId: user.sId,
    draftKey: "user-menu",
  });

  const createConversationWithMessage = useCreateConversationWithMessage({
    owner,
    user,
  });

  const { setSelectedAgent } = useContext(InputBarContext);

  const handleAskHelp = () => {
    if (
      router.pathname === "/w/[wId]/conversation/[cId]" ||
      router.pathname.match(/^\/w\/[^/]+\/conversation\/[^/]+$/)
    ) {
      setSelectedAgent({
        type: "agent",
        id: GLOBAL_AGENTS_SID.HELPER,
        label: t("misc.userMenuHelp", "Help"),
        pictureUrl: "/static/systemavatar/helper_avatar_full.png",
        description: t(
          "misc.userMenuHelpDescription",
          "Help on how to use Dasis"
        ),
      });
    } else {
      void router.push(
        getConversationRoute(
          owner.sId,
          "new",
          `agent=${GLOBAL_AGENTS_SID.HELPER}`
        )
      );
    }
  };

  const { submit: handleHelpSubmit } = useSubmitFunction(
    useCallback(
      async (input: string, mentions: MentionType[]) => {
        const inputWithHelp = input.includes("@help")
          ? input
          : `@help ${input.trimStart()}`;
        const mentionsWithHelp = mentions.some(
          (mention) =>
            isAgentMention(mention) &&
            mention.configurationId === GLOBAL_AGENTS_SID.HELPER
        )
          ? mentions
          : [
              ...mentions,
              { configurationId: GLOBAL_AGENTS_SID.HELPER } as AgentMention,
            ];
        const conversationRes = await createConversationWithMessage({
          messageData: {
            input: inputWithHelp.replace(
              "@help",
              serializeMention({ name: "help", sId: GLOBAL_AGENTS_SID.HELPER })
            ),
            mentions: mentionsWithHelp,
            contentFragments: {
              uploaded: [],
              contentNodes: [],
            },
          },
        });
        if (conversationRes.isErr()) {
          sendNotification({
            title: conversationRes.error.title,
            description: conversationRes.error.message,
            type: "error",
          });
        } else {
          void router.push(
            getConversationRoute(owner.sId, conversationRes.value.sId)
          );
        }
      },
      [createConversationWithMessage, owner, router, sendNotification]
    )
  );

  const forceRoleUpdate = useMemo(
    () => async (role: "user" | "builder" | "admin" | "manager") => {
      const result = await forceUserRole(user, owner, role, featureFlags);
      if (result.isOk()) {
        sendNotification({
          title: t("misc.userMenuSuccessTitle", "Success !"),
          description: t(
            "misc.userMenuRoleUpdateReloading",
            "{{message}} (reloading...)",
            {
              message: result.value,
            }
          ),
          type: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        sendNotification({
          title: t("misc.userMenuErrorTitle", "Error !"),
          description: result.error,
          type: "error",
        });
      }
    },
    [owner, sendNotification, user, featureFlags, t]
  );

  const handleSendOnboarding = useMemo(
    () => async () => {
      const result = await sendOnboardingConversation(owner, featureFlags);
      if (result.isOk) {
        window.dispatchEvent(new ConversationsUpdatedEvent());
        sendNotification({
          title: t("misc.userMenuSuccessTitle", "Success !"),
          description: t(
            "misc.userMenuOnboardingCreated",
            "Onboarding conversation created (redirecting...)"
          ),
          type: "success",
        });
        setTimeout(() => {
          void router.push(
            `/w/${owner.sId}/conversation/${result.conversationId}`
          );
        }, 1000);
      } else {
        sendNotification({
          title: t("misc.userMenuErrorTitle", "Error !"),
          description: result.error,
          type: "error",
        });
      }
    },
    [owner, sendNotification, featureFlags, router, t]
  );

  // Check if user has multiple workspaces (from WorkOS orgs, or in dev
  // mode from local DB workspaces as fallback).
  const hasMultipleWorkspaces = useMemo(() => {
    const hasMultipleOrgs =
      !!user.organizations && user.organizations.length > 1;
    const hasMultipleLocalWorkspaces =
      isDevelopment() &&
      !user.organizations?.length &&
      user.workspaces.length > 1;
    return hasMultipleOrgs || hasMultipleLocalWorkspaces;
  }, [user]);

  const inviteUsersQuestion = t(
    "misc.userMenuHowInviteUsers",
    "How do I invite new users?"
  );
  const agentsSlackQuestion = t(
    "misc.userMenuHowUseAgentsSlack",
    "How do I use agents in Slack workflow?"
  );
  const manageBillingQuestion = t(
    "misc.userMenuHowManageBilling",
    "How do I manage billing?"
  );

  return (
    <>
      <UserSettingsPopover
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        owner={owner}
      />
      <DropdownMenu>
        <DropdownMenuTrigger className="hover:bg-hover data-[state=open]:bg-selected rounded-xl p-2 m-2">
          <div className="group flex cursor-pointer items-center justify-between gap-2">
            <span className="sr-only">
              {t("misc.userMenuOpenUserMenu", "Open user menu")}
            </span>
            <div className="flex gap-2 items-center">
              <Avatar
                size="sm"
                visual={
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  user.image
                    ? user.image
                    : "https://gravatar.com/avatar/anonymous?d=mp"
                }
                clickable
                isRounded
              />
              <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                <span
                  className={cn(
                    "heading-sm w-full truncate transition-colors",
                    "text-foreground"
                  )}
                >
                  {user.firstName}
                </span>
                <span className="-mt-0.5 w-full truncate text-sm text-muted-foreground">
                  {owner.name}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Icon
                visual={ChevronDown}
                className="text-muted-foreground group-hover:text-primary-400 group-active:text-primary-950"
              />
            </div>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="end"
          sideOffset={8}
          className="w-64"
        >
          {hasMultipleWorkspaces && (
            <>
              <DropdownMenuLabel
                label={t("misc.userMenuWorkspace", "Workspace")}
              />
              <WorkspacePickerRadioGroup user={user} workspace={owner} />
              <Separator className="my-1" />
            </>
          )}

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              label={t("misc.userMenuHelp", "Help")}
              icon={Heart}
            />
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuLabel
                  label={t("misc.userMenuAskQuestions", "Ask questions")}
                />
                <DropdownMenuItem
                  label={t("misc.userMenuAskHelp", "Ask @help")}
                  icon={MessageChatCircle}
                  onClick={() => void handleAskHelp()}
                />
                <DropdownMenuItem
                  label={inviteUsersQuestion}
                  icon={MessageTextCircle01}
                  onClick={() => void handleHelpSubmit(inviteUsersQuestion, [])}
                />
                <DropdownMenuItem
                  label={agentsSlackQuestion}
                  icon={MessageTextCircle01}
                  onClick={() => void handleHelpSubmit(agentsSlackQuestion, [])}
                />
                <DropdownMenuItem
                  label={manageBillingQuestion}
                  icon={MessageTextCircle01}
                  onClick={() =>
                    void handleHelpSubmit(manageBillingQuestion, [])
                  }
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {subscription?.plan.limits.canUseProduct && (
            <>
              <DropdownMenuItem
                label={t(
                  "misc.userMenuExploratoryFeatures",
                  "Exploratory features"
                )}
                icon={Beaker02}
                href={`/w/${owner.sId}/labs`}
              />
              <Separator className="my-1" />
            </>
          )}

          <DropdownMenuLabel label={t("misc.userMenuAccount", "Account")} />
          {subscription?.plan.limits.canUseProduct && (
            <DropdownMenuItem
              label={t("misc.userMenuPersonalSettings", "Personal Settings")}
              icon={User01}
              onSelect={() => setSettingsOpen(true)}
            />
          )}

          <DropdownMenuItem
            label={t("misc.userMenuSignOut", "Sign out")}
            icon={LogOut01}
            onClick={() => {
              // Clear all conversation drafts for this user.
              clearAllDraftsFromUser();

              datadogLogs.clearUser();
              window.DD_RUM?.onReady(() => {
                window.DD_RUM?.clearUser();
              });
              window.location.href = `${config.getApiBaseUrl()}/api/workos/logout`;
            }}
          />

          {showDebugTools(featureFlags) && (
            <>
              <Separator className="my-1" />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  label={t("misc.userMenuDevTools", "Dev Tools")}
                  icon={Shapes}
                />
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {(router.pathname === "/w/[wId]/conversation/[cId]" ||
                      router.pathname.match(
                        /^\/w\/[^/]+\/conversation\/[^/]+$/
                      )) && (
                      <DropdownMenuItem
                        label={t(
                          "misc.userMenuDebugConversation",
                          "Debug conversation"
                        )}
                        onClick={() => {
                          const regexp = new RegExp(
                            `/w/([^/]+)/conversation/([^/]+)`
                          );
                          const match = window.location.href.match(regexp);
                          if (match) {
                            window.open(
                              `/poke/${match[1]}/conversation/${match[2]}`,
                              "_blank"
                            );
                          }
                        }}
                        icon={Shapes}
                      />
                    )}
                    {!isOnlyAdmin(owner) && (
                      <DropdownMenuItem
                        label={t("misc.userMenuBecomeAdmin", "Become Admin")}
                        onClick={() => forceRoleUpdate("admin")}
                        icon={Star01}
                      />
                    )}
                    {!isOnlyManager(owner) && (
                      <DropdownMenuItem
                        label={t(
                          "misc.userMenuBecomeManager",
                          "Become Manager"
                        )}
                        onClick={() => forceRoleUpdate("manager")}
                        icon={Star01}
                      />
                    )}
                    {!isOnlyBuilder(owner) && (
                      <DropdownMenuItem
                        label={t(
                          "misc.userMenuBecomeBuilder",
                          "Become Builder"
                        )}
                        onClick={() => forceRoleUpdate("builder")}
                        icon={Lightbulb04}
                      />
                    )}
                    {!isOnlyUser(owner) && (
                      <DropdownMenuItem
                        label={t("misc.userMenuBecomeUser", "Become User")}
                        onClick={() => forceRoleUpdate("user")}
                        icon={User01}
                      />
                    )}
                    <DropdownMenuItem
                      label={
                        privacyMask.isEnabled
                          ? t(
                              "misc.userMenuDisablePrivacyMask",
                              "Disable Privacy Mask"
                            )
                          : t(
                              "misc.userMenuEnablePrivacyMask",
                              "Enable Privacy Mask"
                            )
                      }
                      onClick={privacyMask.toggle}
                      icon={privacyMask.isEnabled ? EyeOff : Eye}
                    />
                    <DropdownMenuItem
                      label={
                        devMode.isEnabled
                          ? t(
                              "misc.userMenuDisableDevConsole",
                              "Disable Dev Console"
                            )
                          : t(
                              "misc.userMenuEnableDevConsole",
                              "Enable Dev Console"
                            )
                      }
                      onClick={devMode.toggle}
                      icon={Terminal}
                    />
                    {owner.role === "admin" && (
                      <DropdownMenuItem
                        label={t(
                          "misc.userMenuSendOnboardingConversation",
                          "Send onboarding conversation"
                        )}
                        onClick={handleSendOnboarding}
                        icon={MessagePlusCircle}
                      />
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
